import React, { useState } from 'react';
import { LOOTBOX_RARITIES } from '../../constants/lootbox';
import { VfxSpan } from '../../utils/helpers';
import { VFX_EFFECTS } from '../../constants/vfx';
import ItemIcon from '../lootbox/ItemIcon';

function SellItems({ inventory, onListItem, myListings, onCancelListing, isS2 }) {
  const [selectedItem, setSelectedItem] = useState(null); // Now stores the actual item object, not index
  const [price, setPrice] = useState('');
  const [auctionMode, setAuctionMode] = useState(false);
  const [auctionDuration, setAuctionDuration] = useState(24);
  const [currencyType, setCurrencyType] = useState("crewmate");
  const [sellTooltip, setSellTooltip] = useState(null); // { item, x, y, type: 'st' | 'vfx' }

  // Sorting and filtering state
  const [sortBy, setSortBy] = useState('name'); // 'name', 'rarity', 'price'
  const [filterStatTrack, setFilterStatTrack] = useState('all'); // 'all', 'stattrack', 'non-stattrack'
  const [filterShiny, setFilterShiny] = useState('all'); // 'all', 'shiny', 'non-shiny'
  const [filterRarity, setFilterRarity] = useState('all'); // 'all' or specific rarity
  const [filterVfx, setFilterVfx] = useState('all'); // 'all', 'vfx', 'non-vfx'
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false); // Toggle for duplicates

  // Filter out items that are currently listed
  const listedItemIds = new Set();
  myListings.filter(l => l.status === "active").forEach(listing => {
    if (listing.item) {
      // Create a unique key for each item based on unboxedAt timestamp
      const key = `${listing.item.id}_${listing.item.rarity}_${listing.item.isShiny || false}_${listing.item.unboxedAt}`;
      listedItemIds.add(key);
    }
  });

  // Filter inventory to exclude currently listed items
  const availableInventory = inventory.filter(item => {
    const key = `${item.id}_${item.rarity}_${item.isShiny || false}_${item.unboxedAt}`;
    return !listedItemIds.has(key);
  });

  // Apply filters and sorting
  const filteredAndSortedInventory = React.useMemo(() => {
    try {
      // Filter out invalid items first
      let result = availableInventory.filter(item => {
        return item && item.rarity && item.name && LOOTBOX_RARITIES[item.rarity];
      });

      // Apply Duplicates filter
      if (showDuplicatesOnly) {
        // Count occurrences of each item (by id, rarity, stattrack, shiny)
        const itemCounts = {};
        result.forEach(item => {
          const key = `${item.id}_${item.rarity}_${item.isStatTrak || false}_${item.isShiny || false}_${item.vfx || 'none'}`;
          itemCounts[key] = (itemCounts[key] || 0) + 1;
        });
        // Only keep items that appear more than once
        result = result.filter(item => {
          const key = `${item.id}_${item.rarity}_${item.isStatTrak || false}_${item.isShiny || false}_${item.vfx || 'none'}`;
          return itemCounts[key] > 1;
        });
      }

      // Apply StatTrak filter
      if (filterStatTrack === 'stattrack') {
        result = result.filter(item => item.isStatTrak);
      } else if (filterStatTrack === 'non-stattrack') {
        result = result.filter(item => !item.isStatTrak);
      }

      // Apply Shiny filter
      if (filterShiny === 'shiny') {
        result = result.filter(item => item.isShiny);
      } else if (filterShiny === 'non-shiny') {
        result = result.filter(item => !item.isShiny);
      }

      // Apply Rarity filter
      if (filterRarity !== 'all') {
        result = result.filter(item => item.rarity === filterRarity);
      }

      // Apply VFX filter
      if (filterVfx === 'vfx') {
        result = result.filter(item => item.vfx && item.vfx.length > 0);
      } else if (filterVfx === 'non-vfx') {
        result = result.filter(item => !item.vfx || item.vfx.length === 0);
      }

      // Apply sorting
      result.sort((a, b) => {
        if (sortBy === 'name') {
          const nameA = a.name || '';
          const nameB = b.name || '';
          return nameA.localeCompare(nameB);
        } else if (sortBy === 'rarity') {
          const rarityOrder = { 'NUCLEAR': 8, 'GALAXY': 7, 'MYTHIC': 6, 'LEGENDARY': 5, 'EPIC': 4, 'RARE': 3, 'UNCOMMON': 2, 'COMMON': 1 };
          const rarityA = rarityOrder[a.rarity] || 0;
          const rarityB = rarityOrder[b.rarity] || 0;
          const rarityDiff = rarityB - rarityA;
          if (rarityDiff !== 0) return rarityDiff;
          const nameA = a.name || '';
          const nameB = b.name || '';
          return nameA.localeCompare(nameB);
        }
        return 0;
      });

      return result;
    } catch (error) {
      console.error("Error filtering/sorting inventory:", error);
      return [];
    }
  }, [availableInventory, sortBy, filterStatTrack, filterShiny, filterRarity, filterVfx, showDuplicatesOnly]);

  if (availableInventory.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-icon">🎒</div>
        <h2>No Items to Sell</h2>
        <p>Open lootboxes to get items to list on the marketplace!</p>
      </div>
    );
  }

  return (
    <div className="sell-items-container">
      <div className="sell-items-section">
        <h3>Select Item to List</h3>

        {/* Sorting and Filtering Controls */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          marginBottom: '20px',
          padding: '16px',
          background: 'var(--bg-card)',
          borderRadius: '8px',
          border: '1px solid var(--border-color)'
        }}>
          {/* Sort By */}
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'var(--text-primary)' }}>Sort By:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid var(--border-color)',
                background: '#1a1a1a',
                color: '#ffffff'
              }}
            >
              <option value="name" style={{ background: '#1a1a1a', color: '#ffffff' }}>Name (A-Z)</option>
              <option value="rarity" style={{ background: '#1a1a1a', color: '#ffffff' }}>Rarity (High to Low)</option>
            </select>
          </div>

          {/* StatTrak Filter */}
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'var(--text-primary)' }}>StatTrak:</label>
            <select
              value={filterStatTrack}
              onChange={(e) => setFilterStatTrack(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid var(--border-color)',
                background: '#1a1a1a',
                color: '#ffffff'
              }}
            >
              <option value="all" style={{ background: '#1a1a1a', color: '#ffffff' }}>All</option>
              <option value="stattrack" style={{ background: '#1a1a1a', color: '#ffffff' }}>StatTrak Only</option>
              <option value="non-stattrack" style={{ background: '#1a1a1a', color: '#ffffff' }}>Non-StatTrak</option>
            </select>
          </div>

          {/* Shiny Filter */}
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'var(--text-primary)' }}>Shiny:</label>
            <select
              value={filterShiny}
              onChange={(e) => setFilterShiny(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid var(--border-color)',
                background: '#1a1a1a',
                color: '#ffffff'
              }}
            >
              <option value="all" style={{ background: '#1a1a1a', color: '#ffffff' }}>All</option>
              <option value="shiny" style={{ background: '#1a1a1a', color: '#ffffff' }}>Shiny Only</option>
              <option value="non-shiny" style={{ background: '#1a1a1a', color: '#ffffff' }}>Non-Shiny</option>
            </select>
          </div>

          {/* Rarity Filter */}
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'var(--text-primary)' }}>Rarity:</label>
            <select
              value={filterRarity}
              onChange={(e) => setFilterRarity(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid var(--border-color)',
                background: '#1a1a1a',
                color: '#ffffff'
              }}
            >
              <option value="all" style={{ background: '#1a1a1a', color: '#ffffff' }}>All Rarities</option>
              {!isS2 && <option value="NUCLEAR" style={{ background: '#1a1a1a', color: '#ffffff' }}>Nuclear</option>}
              <option value="GALAXY" style={{ background: '#1a1a1a', color: '#ffffff' }}>Galaxy</option>
              <option value="MYTHIC" style={{ background: '#1a1a1a', color: '#ffffff' }}>Mythic</option>
              <option value="LEGENDARY" style={{ background: '#1a1a1a', color: '#ffffff' }}>Legendary</option>
              <option value="EPIC" style={{ background: '#1a1a1a', color: '#ffffff' }}>Epic</option>
              <option value="RARE" style={{ background: '#1a1a1a', color: '#ffffff' }}>Rare</option>
              <option value="UNCOMMON" style={{ background: '#1a1a1a', color: '#ffffff' }}>Uncommon</option>
              <option value="COMMON" style={{ background: '#1a1a1a', color: '#ffffff' }}>Common</option>
            </select>
          </div>

          {/* VFX Filter */}
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'var(--text-primary)' }}>VFX:</label>
            <select
              value={filterVfx}
              onChange={(e) => setFilterVfx(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid var(--border-color)',
                background: '#1a1a1a',
                color: '#ffffff'
              }}
            >
              <option value="all" style={{ background: '#1a1a1a', color: '#ffffff' }}>All</option>
              <option value="vfx" style={{ background: '#1a1a1a', color: '#ffffff' }}>VFX Only</option>
              <option value="non-vfx" style={{ background: '#1a1a1a', color: '#ffffff' }}>No VFX</option>
            </select>
          </div>

          {/* Duplicates Only Toggle */}
          <div style={{ flex: '1 1 200px', display: 'flex', alignItems: 'flex-end' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              padding: '8px 12px',
              background: showDuplicatesOnly ? 'var(--primary-color)' : 'transparent',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              transition: 'all 0.2s',
              userSelect: 'none'
            }}>
              <input
                type="checkbox"
                checked={showDuplicatesOnly}
                onChange={(e) => setShowDuplicatesOnly(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', fontWeight: 'bold' }}>Duplicates Only</span>
            </label>
          </div>
        </div>

        <div className="inventory-grid">
          {filteredAndSortedInventory.map((item) => {
            const rarity = LOOTBOX_RARITIES[item.rarity];
            const isSelected = selectedItem && selectedItem.unboxedAt === item.unboxedAt && selectedItem.id === item.id;
            const isShinyGalaxy = item.isShiny && item.rarity === 'GALAXY';
            const hasCooldown = item.purchasedAt && (Date.now() - item.purchasedAt) < 24 * 60 * 60 * 1000;

            return (
              <div
                key={`${item.id}_${item.unboxedAt || Date.now()}`}
                className={`inventory-item ${rarity.glow ? 'glow' : ''} ${isShinyGalaxy ? 'shiny-galaxy' : item.isShiny ? 'shiny' : ''} ${isSelected ? 'selected' : ''}`}
                style={{
                  borderColor: isShinyGalaxy ? 'transparent' : rarity.color,
                  boxShadow: isShinyGalaxy
                    ? '' // Handled by shiny-galaxy animation
                    : (rarity.glow ? `0 0 15px ${rarity.color}` : 'none'),
                  position: 'relative'
                }}
                onClick={() => setSelectedItem(item)}
                onMouseEnter={(e) => {
                  if (item.isStatTrak) setSellTooltip({ item, x: e.clientX, y: e.clientY, type: 'st' });
                  else if (item.vfx && item.vfx.length > 0) setSellTooltip({ item, x: e.clientX, y: e.clientY, type: 'vfx' });
                }}
                onMouseMove={(e) => {
                  if (sellTooltip) setSellTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null);
                }}
                onMouseLeave={() => setSellTooltip(null)}
              >
                {isShinyGalaxy ? (
                  <div className="shiny-galaxy-badge">🌌 GALAXY ✨</div>
                ) : item.isShiny && (
                  <div className="shiny-badge-small">✨</div>
                )}
                {item.isStatTrak && (
                  <div style={{
                    position: 'absolute', top: '4px', left: '4px',
                    background: '#FF6B00', padding: '1px 5px', borderRadius: '4px',
                    fontSize: '9px', fontWeight: 'bold', color: '#000'
                  }}>ST</div>
                )}
                {item.vfx && item.vfx.length > 0 && (
                  <div style={{
                    position: 'absolute', bottom: '4px', right: '4px',
                    background: 'rgba(167,139,250,0.2)', border: '1px solid #a78bfa',
                    borderRadius: '4px', padding: '1px 5px',
                    fontSize: '9px', fontWeight: 'bold', color: '#a78bfa'
                  }}>{item.vfx.length > 1 ? ` x${item.vfx.length}` : ' VFX'}</div>
                )}
                {hasCooldown && (
                  <div style={{
                    position: 'absolute', bottom: '4px', left: '4px',
                    background: 'rgba(239,68,68,0.2)', border: '1px solid #ef4444',
                    borderRadius: '4px', padding: '1px 5px',
                    fontSize: '9px', fontWeight: 'bold', color: '#ef4444'
                  }}>⏳ {Math.ceil((24 * 60 * 60 * 1000 - (Date.now() - item.purchasedAt)) / (60 * 60 * 1000))}h</div>
                )}
                <div className="item-icon">
                  {item.vfx && item.vfx.length > 0
                    ? <VfxSpan vfx={item.vfx}><ItemIcon item={item} size={48} /></VfxSpan>
                    : <ItemIcon item={item} size={48} />}
                </div>
                <div className={`item-name ${rarity.rainbow && !item.isShiny ? 'rainbow-glow' : ''}`} style={{ color: item.isShiny ? '#FFD700' : rarity.color }}>
                  {item.vfx && item.vfx.length > 0
                    ? <VfxSpan vfx={item.vfx}>{item.name}</VfxSpan>
                    : item.name}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sell screen hover tooltip */}
      {sellTooltip && (
        <div style={{
          position: 'fixed',
          left: sellTooltip.x + 14,
          top: sellTooltip.y + 14,
          zIndex: 9999,
          background: 'rgba(20,20,20,0.97)',
          border: `2px solid ${sellTooltip.type === 'st' ? '#FF8C00' : '#a78bfa'}`,
          borderRadius: '10px',
          padding: '12px 16px',
          pointerEvents: 'none',
          minWidth: '160px',
          boxShadow: `0 4px 24px ${sellTooltip.type === 'st' ? 'rgba(255,140,0,0.3)' : 'rgba(167,139,250,0.3)'}`
        }}>
          {sellTooltip.type === 'st' ? (
            <>
              <div style={{ color: '#FF8C00', fontWeight: 'bold', fontSize: '13px', marginBottom: '8px' }}>📊 StatTrak™ Stats</div>
              {(sellTooltip.item.trackedStats || []).map(stat => {
                const value = sellTooltip.item.statValues?.[stat] || 0;
                const label = stat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                return (
                  <div key={stat} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', fontSize: '12px', marginBottom: '4px' }}>
                    <span style={{ color: '#aaa' }}>{label}</span>
                    <span style={{ color: '#FF8C00', fontWeight: 'bold' }}>{value}</span>
                  </div>
                );
              })}
              {(!sellTooltip.item.trackedStats || sellTooltip.item.trackedStats.length === 0) && (
                <div style={{ color: '#666', fontSize: '12px' }}>No stats tracked yet</div>
              )}
            </>
          ) : (
            <>
              <div style={{ color: '#a78bfa', fontWeight: 'bold', fontSize: '13px', marginBottom: '8px' }}>✦ VFX Effects</div>
              {(Array.isArray(sellTooltip.item.vfx) ? sellTooltip.item.vfx : [sellTooltip.item.vfx]).map((effectId, idx) => {
                const effect = VFX_EFFECTS.find(e => e.id === effectId);
                return (
                  <div key={idx} style={{ fontSize: '12px', marginBottom: '4px', color: '#c4b5fd' }}>
                    <span style={{ fontWeight: 'bold' }}>{effect ? effect.name : effectId}</span>
                    {effect && <span style={{ color: '#888', marginLeft: '6px' }}>— {effect.description}</span>}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {selectedItem !== null && selectedItem && LOOTBOX_RARITIES[selectedItem.rarity] && (
        <div className="modal-overlay" onClick={() => setSelectedItem(null)}>
          <div className="modal-content sell-item-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedItem(null)}>×</button>

            <div className="sell-modal-header">
              <div className="sell-modal-icon"><ItemIcon item={selectedItem} size={48} /></div>
              <div className="sell-modal-title">
                <h2>{selectedItem.name || 'Unknown Item'}</h2>
                <div className="sell-modal-rarity" style={{ color: LOOTBOX_RARITIES[selectedItem.rarity].color }}>
                  {LOOTBOX_RARITIES[selectedItem.rarity].name}
                  {selectedItem.isShiny && " ✨ SHINY"}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Listing Type:</label>
              <div className="listing-type-buttons">
                <button
                  className={`listing-type-btn ${!auctionMode ? "active" : ""}`}
                  onClick={() => setAuctionMode(false)}
                >
                  💰 Buy Now (Fixed Price)
                </button>
                <button
                  className={`listing-type-btn ${auctionMode ? "active" : ""}`}
                  onClick={() => setAuctionMode(true)}
                >
                  🔨 Auction (Accept Bids)
                </button>
              </div>
            </div>

            {auctionMode && (
              <div className="form-group">
                <label>Auction Duration:</label>
                <div className="listing-type-buttons" style={{ flexWrap: 'wrap' }}>
                  {[
                    { value: 1, label: '1h' },
                    { value: 6, label: '6h' },
                    { value: 12, label: '12h' },
                    { value: 24, label: '24h' },
                    { value: 48, label: '2d' },
                    { value: 72, label: '3d' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      className={`listing-type-btn ${auctionDuration === opt.value ? "active" : ""}`}
                      onClick={() => setAuctionDuration(opt.value)}
                      style={{ minWidth: '60px', flex: '0 0 auto' }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Currency Type:</label>
              <div className="currency-selector">
                <button
                  className={`currency-select-btn ${currencyType === "crewmate" ? "active" : ""}`}
                  onClick={() => setCurrencyType("crewmate")}
                >
                  🔵 Crewmate Tokens
                </button>
                <button
                  className={`currency-select-btn ${currencyType === "imposter" ? "active" : ""}`}
                  onClick={() => setCurrencyType("imposter")}
                >
                  🔴 Imposter Tokens
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>{auctionMode ? 'Starting Bid:' : 'Price:'}</label>
              <input
                type="number"
                className="price-input"
                placeholder="Enter price in tokens"
                min="0.01"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
              {selectedItem?.purchasedAt && (Date.now() - selectedItem.purchasedAt) < 24 * 60 * 60 * 1000 && (
                <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                  ⏳ This item was recently purchased and cannot be listed for {Math.ceil((24 * 60 * 60 * 1000 - (Date.now() - selectedItem.purchasedAt)) / (60 * 60 * 1000))} more hour(s).
                </div>
              )}
            </div>

            <button
              className="btn-list"
              disabled={selectedItem?.purchasedAt && (Date.now() - selectedItem.purchasedAt) < 24 * 60 * 60 * 1000}
              onClick={() => {
                const priceNum = parseFloat(price);
                if (priceNum > 0) {
                  onListItem(selectedItem, priceNum, auctionMode, currencyType, auctionMode ? auctionDuration : undefined);
                  setSelectedItem(null);
                  setPrice('');
                  setCurrencyType("crewmate");
                  setAuctionMode(false);
                  setAuctionDuration(24);
                }
              }}
            >
              List Item for {price || '?'} {currencyType === "crewmate" ? "🔵" : "🔴"}
            </button>
          </div>
        </div>
      )}

      {myListings.length > 0 && (
        <div className="my-listings-section">
          <h3>My Active Listings</h3>
          <div className="my-listings">
            {myListings.filter(l => l.status === "active" && l.item && l.item.rarity && LOOTBOX_RARITIES[l.item.rarity]).map(listing => {
              const rarity = LOOTBOX_RARITIES[listing.item.rarity];
              return (
                <div key={listing.id} className="my-listing-card">
                  <div className="my-listing-icon"><ItemIcon item={listing.item} size={48} /></div>
                  <div className="my-listing-info">
                    <div className="my-listing-name" style={{ color: rarity.color }}>
                      {listing.item.name}
                    </div>
                    <div className="my-listing-price">
                      {listing.auctionMode ? `Current: ${listing.currentBid || 0}` : `Price: ${listing.price}`} 🪙
                    </div>
                  </div>
                  <button
                    className="btn-cancel"
                    onClick={() => onCancelListing(listing.id)}
                  >
                    Cancel
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default SellItems;
